import { useEffect, useState } from 'preact/hooks';
import type { PersonDetailDto } from '../../shared/types';
import { api, ApiError } from '../api';
import { Artwork } from '../components/Artwork';
import { EpisodeRow } from '../components/EpisodeRow';
import { SkeletonEpisodeList } from '../components/Skeleton';

export function Person({ name }: { name: string }) {
  const [data, setData] = useState<PersonDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .person(name)
      .then((res) => {
        if (cancelled) return;
        setData(res);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Could not find person details.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [name]);

  if (loading) {
    return (
      <div class="view">
        <SkeletonEpisodeList count={4} label="Loading appearances" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div class="view">
        <header class="view__header">
          <a class="button button--ghost" href="/search">
            ← Back to Search
          </a>
        </header>
        <div class="error" style={{ margin: '1rem 0' }}>
          <p>{error ?? 'Person not found.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div class="view">
      <header class="person-header">
        {data.img ? (
          <Artwork src={data.img} alt="" size={120} className="person-header__art" />
        ) : (
          <div class="person-header__avatar" aria-hidden="true">
            {data.name.charAt(0).toUpperCase()}
          </div>
        )}
        <div class="person-header__body">
          <h1>{data.name}</h1>
          <p class="muted">
            {data.role && <span>{data.role}</span>}
            {data.role && data.group && <span> · </span>}
            {data.group && <span>{data.group}</span>}
          </p>
          {data.href && (
            <div style={{ marginTop: '0.5rem' }}>
              <a
                class="button button--ghost"
                href={data.href}
                target="_blank"
                rel="external noopener noreferrer"
              >
                Website / Profile
              </a>
            </div>
          )}
        </div>
      </header>

      {data.shows.length > 0 && (
        <section class="section">
          <h2 class="section__title">Podcasts ({data.shows.length})</h2>
          <div class="podcast-grid">
            {data.shows.map((feed) => (
              <a key={feed.id} class="podcast-card" href={`/podcast/${feed.id}`}>
                <Artwork src={feed.imageUrl} alt="" size={160} />
                <span class="podcast-card__title">{feed.title}</span>
                {feed.author && <span class="podcast-card__author">{feed.author}</span>}
              </a>
            ))}
          </div>
        </section>
      )}

      {data.episodes.length > 0 && (
        <section class="section">
          <h2 class="section__title">Episodes & Appearances ({data.episodes.length})</h2>
          <div class="episode-list">
            {data.episodes.map((episode) => (
              <EpisodeRow key={episode.id} episode={episode} showPodcast showArtwork />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
